// A link to one vessel's service record: /service?vessel=<rowId>, the vessel's Smartsheet row id. The
// Calendar's entries link here, and so can any other page (a notification, a list of boats). The Service
// page opens the vessel in its Board (group and card open, every work showing, scrolled to it), then
// drops the parameter from the address bar. A work's row id lands on the vessel it belongs to.

export const VESSEL_PARAM = 'vessel';

/** the address of a vessel's service record */
export const serviceVesselHref = (rowId: number): string => `/service?${VESSEL_PARAM}=${rowId}`;

/** the row id a ?vessel= value names, or null when it isn't one (Smartsheet row ids are positive whole
 *  numbers, well inside what a JavaScript number holds exactly). */
export function parseVesselParam(value: string | null | undefined): number | null {
  const s = (value ?? '').trim();
  if (!/^\d{1,16}$/.test(s)) return null;
  const n = Number(s);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

/** the vessel a row belongs to: the row itself when it's a vessel (right under a top-level group), the
 *  vessel above it when it's a work or sub-work. Undefined for a group row and a row not on the sheet. */
export function vesselOfRow<R extends { id: number; parentId: number | null }>(rows: readonly R[], id: number): R | undefined {
  const byId = new Map(rows.map((r) => [r.id, r]));
  let row = byId.get(id);
  // a malformed sheet can't loop forever: a sheet is never deeper than its row count
  for (let hops = 0; row && row.parentId != null && hops <= rows.length; hops++) {
    const parent = byId.get(row.parentId);
    if (!parent) return undefined;
    if (parent.parentId == null) return row;
    row = parent;
  }
  return undefined;
}

/** the address bar's parameters without ?vessel=, everything else kept */
export function withoutVesselParam(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(VESSEL_PARAM);
  return next;
}

/** what the Service page does next with a ?vessel= link, given the grid it holds:
 *  - open: the vessel is on it (a cached grid is fine for that);
 *  - refetch / wait: it isn't, but the grid is older than the link, so it may predate a vessel added just
 *    now (the new-vessel notification links straight to one). Fetch once, then wait for a fresh grid. A
 *    fetch that fails leaves the link waiting for the next poll or Sync, with the sync error showing.
 *  - gone: a grid fetched after the link arrived still hasn't got it. Only then is it "not on the sheet". */
export type VesselLinkStep = 'open' | 'refetch' | 'wait' | 'gone';
export function vesselLinkStep(s: {
  /** the linked vessel is on the grid */
  found: boolean;
  /** when the link arrived, and when the grid was last updated (ms) */
  linkedAt: number;
  updatedAt: number;
  /** a fetch of the grid is running */
  fetching: boolean;
  /** this link already asked for one */
  refetched: boolean;
}): VesselLinkStep {
  if (s.found) return 'open';
  if (s.updatedAt >= s.linkedAt) return 'gone';
  if (s.fetching || s.refetched) return 'wait';
  return 'refetch';
}

// the person scrolling, tapping or typing ends revealVesselCard's watch: never fight them for the page
const TAKE_OVER = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

/** where an element sits in its scroller's content, whatever that scroller's scrolled to: it only moves
 *  when something above it changes height */
function contentTop(el: Element): number {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && p.scrollHeight > p.clientHeight) {
      return el.getBoundingClientRect().top - p.getBoundingClientRect().top + p.scrollTop;
    }
  }
  return el.getBoundingClientRect().top + window.scrollY;
}

/** bring a vessel's card (its data-vessel-id) to the top of the page. The card may not be drawn for a
 *  frame or two (a group opening, or a cleared search, which filters through useDeferredValue), so this
 *  waits for it. And the page may still be settling once it is: on a first visit a font arrives in the
 *  middle of the smooth scroll and the cards above get shorter, leaving the card under the top bar. So for
 *  a moment it aims again whenever the card moves, until the person takes over (or the card goes). */
export function revealVesselCard(id: number, { waitFrames = 60, settleMs = 1500 } = {}): void {
  let raf = 0;
  let frames = 0;
  let aimedAt: number | null = null;
  let until = 0;
  const stop = () => {
    cancelAnimationFrame(raf);
    for (const e of TAKE_OVER) window.removeEventListener(e, stop, true);
  };
  const tick = () => {
    const el = document.querySelector(`[data-vessel-id="${id}"]`);
    if (!el) {
      // not drawn yet: try again next frame, for a second at most (or it's gone again, then stop)
      if (aimedAt == null && ++frames <= waitFrames) raf = requestAnimationFrame(tick);
      else stop();
      return;
    }
    const top = contentTop(el);
    if (aimedAt == null || Math.abs(top - aimedAt) > 1) {
      if (aimedAt == null) until = performance.now() + settleMs;
      aimedAt = top;
      el.scrollIntoView({ block: 'start' });
    }
    if (performance.now() < until) raf = requestAnimationFrame(tick);
    else stop();
  };
  for (const e of TAKE_OVER) window.addEventListener(e, stop, { capture: true, passive: true });
  // two frames: the board has rendered and laid out what the click opened
  raf = requestAnimationFrame(() => { raf = requestAnimationFrame(tick); });
}
